import { ecId, ecLean, handBack, HandBack, itemField, record, type JanusCtx, type Row } from './context.js';
import { skipUnported } from '../unported.js';
import { collectTriggerCandidates, TriggerInputUnavailable, type TriggerEvent } from '../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';
import { runTriggerFamily, triggerFamilyPreDraw } from './trigger-executor.js';
import { triggerAllyReactionsAgainstIncomingRoll } from '../port/k4-dispatch/trigger_ally_reactions_against_incoming_roll.js';
import { triggerOpportunityAttacksWhenTargetStandsUpFromProne } from '../port/k4-dispatch/trigger_opportunity_attacks_when_target_stands_up_from_prone.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import { nativePortValue } from '../port/native-value.js';
import type { E } from '../port/k4-dispatch/support-b2.js';

function environment(ctx: JanusCtx, target: Row, event: TriggerEvent): E {
  return browserDependencies({
    encounter_creature_field: (value: unknown, key: string) => ecLean({ encounter_creatures: value }, key),
    // K4's retired server-dispatch seam now delegates to the shared browser executor.
    ft_skip_server_rule: (_reason: unknown, value: unknown) => {
      const owner = record(value);
      if (!owner) handBack('k4-reaction:owner-not-held');
      try {
        const candidates = collectTriggerCandidates(janusCtxTriggerReaders(ctx), owner, event);
        if (!ctx.read.has(owner, event.trigger) && !candidates.carriers.length && !candidates.statuses.length) return;
        triggerFamilyPreDraw(ctx, owner, event, undefined, target, true);
      } catch (error) {
        // This is an owed bystander boundary, not the player's attack. Discovery
        // throws only for a held carrier/status; empty inventories returned above.
        // Keep optional consent pending and never swallow a partial execution.
        if (!(error instanceof TriggerInputUnavailable || error instanceof HandBack)) throw error;
        if (error.reason.includes('trigger-consent')) throw error;
        const name = String(itemField(record(owner.player_characters), 'player_name')
          ?? ecLean(owner, 'encounter_creature_name') ?? itemField(owner, 'item_name') ?? 'Creature');
        const reason = `${name}: ${event.trigger} skipped (${error.reason.replace(/[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}/gi, 'unavailable definition')})`;
        if (!ctx.rows.some(row => row.roll_type_1 === 'janus_unported_skip' && row.roll_by === ecId(owner) && row.rolls_full === reason)) {
          const skipped = { roll_type_1: 'janus_unported_skip' as const, roll_type_2: null, roll_result: 0, roll_private: 1 as const,
            roll_item_id: null, roll_by: ecId(owner), roll_to: null, rolls_full: reason };
          ctx.rows.push(ctx.dryRun ? skipped : { ...skipUnported(reason), roll_by: ecId(owner) });
        }
        return;
      }
      // This admitted reactor also needs its held inventory during nested self
      // dispatch. Keep that certification local to this boundary and this owner.
      const prior = ctx.read;
      ctx.read = { ...prior, reactionInventoryComplete: row => prior.reactionInventoryComplete(row)
        || (ecId(row) === ecId(owner) && prior.bystanderReactionInventoryHeld?.(row) === true) };
      try { runTriggerFamily(ctx, event, owner, target, [target], false, undefined, true); }
      finally { ctx.read = prior; }
    },
  }) as unknown as E;
}
function encounter(ctx: JanusCtx): { value: Row } {
  return { value: { ...ctx.encounter, encounter_creatures_arr: [...ctx.members.values()] } };
}
export function runIncomingAllyReactions(ctx: JanusCtx, attacker: Row, target: Row, rollType: string): void {
  const event = { trigger: 'triggers_on_roll_type_against_the_target', rollType };
  nativePortValue(triggerAllyReactionsAgainstIncomingRoll({ value: attacker }, { value: target }, { value: [] }, '', rollType,
    encounter(ctx), null, environment(ctx, target, event)));
}
export function runStandingOpportunityReactions(ctx: JanusCtx, standing: Row): void {
  nativePortValue(triggerOpportunityAttacksWhenTargetStandsUpFromProne({ value: standing }, { value: [] }, '', encounter(ctx), null,
    environment(ctx, standing, { trigger: 'triggers_when_target_stands_up_from_prone' })));
}
